'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { User } from '@/lib/auth'
import { useAuth } from '@/contexts/AuthContext'
import { getUserFavoriteServiceIds, updateUserFavoriteServices } from '@/lib/api'
import { fetchMarketplaceServices, type MarketplaceService } from '@/lib/marketplace-services'

const STORAGE_KEY = 'planner-favorite-service-ids'

function readLocal(): number[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.map(Number).filter((n) => Number.isFinite(n) && n > 0)
  } catch {
    return []
  }
}

function writeLocal(ids: number[]) {
  if (typeof window === 'undefined') return
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify([...new Set(ids)].sort((a, b) => a - b))
  )
}

function parseServiceId(x: unknown): number | undefined {
  if (typeof x === 'number' && Number.isFinite(x) && x > 0) return x
  if (x && typeof x === 'object' && x !== null) {
    const o = x as Record<string, unknown>
    if ('id' in o) {
      const id = o.id
      if (typeof id === 'number' && Number.isFinite(id) && id > 0) return id
      if (typeof id === 'string') {
        const n = Number(id)
        if (Number.isFinite(n) && n > 0) return n
      }
    }
    if (o.attributes && typeof o.attributes === 'object' && o.attributes !== null) {
      return parseServiceId(o.attributes)
    }
  }
  return undefined
}

function idsFromUser(user: User | null): number[] {
  if (!user?.favorite_services) return []
  let raw: unknown = user.favorite_services
  if (
    raw &&
    typeof raw === 'object' &&
    raw !== null &&
    'data' in raw &&
    Array.isArray((raw as { data: unknown }).data)
  ) {
    raw = (raw as { data: unknown[] }).data
  }
  if (!Array.isArray(raw)) return []
  return raw
    .map((x) => parseServiceId(x))
    .filter((n): n is number => typeof n === 'number')
}

export type FavoriteServicesContextValue = {
  favoriteIds: Set<number>
  toggleFavorite: (service: MarketplaceService) => Promise<void>
  isFavorite: (serviceId: number) => boolean
}

const FavoriteServicesContext = createContext<FavoriteServicesContextValue | undefined>(
  undefined
)

export function FavoriteServicesProvider({ children }: { children: ReactNode }) {
  const { user, token, refreshUser } = useAuth()

  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(() => new Set(readLocal()))
  const favoriteIdsRef = useRef<Set<number>>(new Set(readLocal()))
  const refreshUserRef = useRef(refreshUser)
  refreshUserRef.current = refreshUser

  useEffect(() => {
    favoriteIdsRef.current = favoriteIds
  }, [favoriteIds])

  useEffect(() => {
    let cancelled = false

    ;(async () => {
      // ── 1. Collect IDs from localStorage (and optionally the API) ──────────
      const fromLocal = readLocal()
      let fromApi: number[] = user?.id ? idsFromUser(user) : []

      if (user?.id && token) {
        try {
          fromApi = await getUserFavoriteServiceIds(token)
        } catch (e) {
          console.warn('Fetch favorites from API failed, using user payload:', e)
        }
      }

      if (cancelled) return

      const merged = [...new Set([...fromApi, ...fromLocal])].sort((a, b) => a - b)

      // ── 2. Sanitize: remove IDs that no longer exist as real services ───────
      // Skip the (potentially large) marketplace fetch when there is nothing
      // to validate — most first-time visitors have an empty favourites list,
      // so this saves one extra network round-trip on every cold page load
      // and a noticeable amount of bandwidth on slow connections.
      let sanitized = merged
      if (merged.length > 0) {
        try {
          const available = await fetchMarketplaceServices()
          if (!cancelled && available.length > 0) {
            const availableIds = new Set(available.map((s) => s.id))
            sanitized = merged.filter((id) => availableIds.has(id))
          }
        } catch (e) {
          console.warn('Could not validate favorites against service list:', e)
        }
      }

      if (cancelled) return

      const prevSorted = [...favoriteIdsRef.current].sort((a, b) => a - b)
      const nextSorted = [...sanitized].sort((a, b) => a - b)
      const unchanged =
        prevSorted.length === nextSorted.length &&
        prevSorted.every((id, i) => id === nextSorted[i])

      const sanitizedSet = new Set(sanitized)
      favoriteIdsRef.current = sanitizedSet

      if (!unchanged) {
        setFavoriteIds(sanitizedSet)
      }

      writeLocal(sanitized)

      // ── 3. Push sanitized list back to API if it changed ──────────────────
      // Do not call refreshUser() here — it replaces `user` and can retrigger
      // unrelated effects; favorites state is already correct in context + localStorage.
      if (user?.id && token) {
        const apiSorted = [...fromApi].sort((a, b) => a - b).join(',')
        const sanitizedSorted = sanitized.join(',')
        if (apiSorted !== sanitizedSorted) {
          updateUserFavoriteServices(token, sanitized).catch((e) =>
            console.warn('Sync favorites to profile:', e)
          )
        }
      }
    })()

    return () => {
      cancelled = true
    }
    // Only `user?.id` + `token` — not `user` (new object every refresh) or `refreshUser` (unstable).
  }, [user?.id, token])

  const toggleFavorite = useCallback(
    async (service: MarketplaceService) => {
      const id = service.id
      const prev = favoriteIdsRef.current
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      const nextArr = Array.from(next).sort((a, b) => a - b)

      favoriteIdsRef.current = next
      setFavoriteIds(next)
      writeLocal(nextArr)

      if (!token || !user?.id) return

      try {
        await updateUserFavoriteServices(token, nextArr)
        await refreshUserRef.current()
      } catch (e) {
        console.error(e)
      }
    },
    [token, user?.id]
  )

  const isFavorite = useCallback(
    (serviceId: number) => favoriteIds.has(serviceId),
    [favoriteIds]
  )

  const value = useMemo(
    () => ({
      favoriteIds,
      toggleFavorite,
      isFavorite,
    }),
    [favoriteIds, toggleFavorite, isFavorite]
  )

  return (
    <FavoriteServicesContext.Provider value={value}>{children}</FavoriteServicesContext.Provider>
  )
}

export function useFavoriteServices(): FavoriteServicesContextValue {
  const ctx = useContext(FavoriteServicesContext)
  if (ctx === undefined) {
    throw new Error('useFavoriteServices must be used within a FavoriteServicesProvider')
  }
  return ctx
}
